"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.propTypes = exports.defaultProps = exports.default = exports.ProductCard = void 0;
require("./ProductCard.css");
var _classnames = _interopRequireDefault(require("classnames"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireDefault(require("react"));
var _renderObject = require("../../util/renderObject/renderObject");
var _ProductCardBody = _interopRequireDefault(require("../ProductCardBody/ProductCardBody"));
var _ProductCardFooter = _interopRequireDefault(require("../ProductCardFooter/ProductCardFooter"));
var _ProductCardHeader = _interopRequireDefault(require("../ProductCardHeader/ProductCardHeader"));
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * ProductCard component
 *
 * @method
 * @param { ProductCardProps} props
 * @returns ProductCard
 */

const ProductCard = props => {
  const {
    /* CardHeader content */
    badge,
    externalMediaElement,
    // Product image coming from web-site
    headerBackgroundColor = defaultProps.headerBackgroundColor,
    tagsTop,
    variantLabel,
    /* CardBody content */
    brandName,
    description,
    eyebrow,
    price,
    productVariant,
    ratings,
    secondValueTag,
    tag,
    tagsBottom,
    title,
    titleSupport,
    valueTag,
    /* CardFooter content */
    externalActionElement,
    externalFooterActionElement,
    sponsoredBadgeLabel,
    /* Card settings */
    actionsGroupBottom,
    backgroundColor = defaultProps.backgroundColor,
    contentAlign,
    contentGap,
    contentPadding,
    fullyClickableParent,
    headerPadding,
    hideFooter,
    imgAlign,
    imgGap,
    isElevated,
    padding,
    theme = defaultProps.theme,
    textAlign
  } = props;

  // only render CardBody if any one of the body contents is passed as prop
  const hasContent = [brandName, description, eyebrow, price, ratings, secondValueTag, tag, title, titleSupport, valueTag].some(Boolean);
  const hasImage = !!externalMediaElement;
  const showFooter = externalFooterActionElement && !hideFooter;
  const hasBodyOrFooter = hasContent || showFooter;
  const componentClassName = (0, _classnames.default)('pal-c-ProductCard', {
    'pal-c-ProductCard--backgroundColor': backgroundColor,
    'pal-c-ProductCard--body': actionsGroupBottom,
    [`pal-c-ProductCard__contentAlign--${contentAlign}`]: contentAlign,
    [`pal-c-ProductCard__contentGap--${contentGap}`]: contentGap,
    [`pal-c-ProductCard__contentPadding--${contentPadding}`]: contentPadding,
    'pal-c-ProductCard--elevated': isElevated,
    [`pal-c-ProductCard__imgAlign--${imgAlign}`]: imgAlign,
    [`pal-c-ProductCard__imgGap--${imgGap}`]: imgGap,
    [`pal-c-ProductCard--oneAction`]: fullyClickableParent,
    [`pal-c-ProductCard__padding--${padding}`]: padding
  });
  const containerStyle = {
    '--backgroundColor': `var( --pal-colors-${theme}-${backgroundColor} )`
  };
  return /*#__PURE__*/_react.default.createElement("div", {
    className: componentClassName,
    style: containerStyle
  }, externalActionElement && /*#__PURE__*/_react.default.createElement(_react.default.Fragment, null, (0, _renderObject.renderObject)(externalActionElement)), hasImage && /*#__PURE__*/_react.default.createElement(_ProductCardHeader.default, {
    badge: badge,
    externalMediaElement: externalMediaElement,
    headerBackgroundColor: headerBackgroundColor,
    headerPadding: headerPadding,
    tagsTop: tagsTop,
    variantLabel: variantLabel
  }), hasBodyOrFooter && /*#__PURE__*/_react.default.createElement("div", {
    className: "pal-c-ProductCard--content"
  }, /*#__PURE__*/_react.default.createElement(_ProductCardBody.default, {
    brandName: {
      text: brandName?.text,
      textColor: brandName?.textColor,
      textStyle: brandName?.textStyle,
      tagName: brandName?.tagName
    },
    contentAlign: contentAlign,
    contentGap: contentGap,
    description: {
      text: description?.text,
      textStyle: description?.textStyle
    },
    eyebrow: eyebrow,
    price: price,
    productVariant: productVariant,
    ratings: ratings,
    secondValueTag: secondValueTag,
    tag: tag,
    tagsBottom: tagsBottom,
    textAlign: textAlign,
    title: {
      text: title?.text,
      textStyle: title?.textStyle,
      tagName: title?.tagName
    },
    titleSupport: titleSupport,
    valueTag: valueTag
  }), showFooter && /*#__PURE__*/_react.default.createElement(_ProductCardFooter.default, {
    alignment: textAlign,
    externalFooterActionElement: externalFooterActionElement,
    sponsoredBadgeLabel: sponsoredBadgeLabel
  })));
};

/**
 * Property type definitions
 * @typedef ProductCardProps
 * @type {object}
 * @property {boolean} actionsGroupBottom - Pushes the footer (action group) to the bottom of the card and body will take up all available space
 * @property {string} additionalOffersText - second valueTag text
 * @property {string} backgroundColor - Sets the backgroundColor of the card
 * @property {string} badge - top left badge text
 * @property {object} brandName - Allows brand prop to accept an object (text, tagName, titleStyle)
 * @property {string} contentAlign - Where the content block should align vertically ('top', 'middle', 'bottom')
 * @property {string} contentGap - The space between the body content and the action group ( 'none', 'sm', 'md', 'lg', 'xl' )
 * @property {string} contentPadding - The padding for the content of the card
 * @property {object} description - Allows description prop to accept an object (text, titleStyle)
 * @property {object} externalActionElement - fully clickable card action from web-site
 * @property {object} externalFooterActionElement - addToBagAction coming from web-site
 * @property {object} externalMediaElement - the image coming from web-site
 * @property {string} eyebrow - Smaller text above the title
 * @property {boolean} fullyClickableParent - boolean if card can be fully clickable
 * @property {string} headerBackgroundColor - The background color for the header
 * @property {string} headerPadding - The padding between the header and the image ('none', 'compact', 'default')
 * @property {boolean} hideFooter - Shows/Hides the footer
 * @property {string} imgAlign - Where in the card the image aligns
 * @property {string} imgGap - The space between image and the content group ('none', 'sm', 'md', 'lg', 'xl')
 * @property {boolean} isElevated - boolean for RailCards that have elevation to add more spacing
 * @property {string} padding - The space between the edge of the card and the content ('none', 'sm', 'md', 'lg')
 * @property {object} price - Object for Price ('variant', 'listPrice', 'salePrice', 'kitPrice')
 * @property {object} productVariant - Object for the product variant ('externalSwatchImage', 'swatchImage', 'variantTypeDescription', 'variantTypeLabel')
 * @property {object} ratings - Object for Ratings ('rating', 'reviewAccessibilityLabel', 'reviewCount')
 * @property {string} secondValueTag - Text for the second value tag
 * @property {string} sponsoredBadgeLabel - Text for the sponsored badge label
 * @property {object} tag - Allows passing an object to the tag prop
 * @property {array} tagsBottom - Allows passing an array of tag objects to display in the card body
 * @property {array} tagsTop - Allows passing an array of tag objects to display in the card header
 * @property {string} textAlign - Where the text content should align horizontally ('left', 'center', 'right')
 * @property {string} theme - Sets the theme for the backgroundColor
 * @property {object} title - Allows title prop to accept an object ('text', 'tagName', 'titleStyle')
 * @property {string} titleSupport - Support text for title
 * @property {string} valueTag - Text for value tag
 * @property {string} variantLabel - support text of the product card
 */
exports.ProductCard = ProductCard;
const propTypes = exports.propTypes = {
  /**
   * Pushes the footer (action group) to the bottom of the card and body will take up all available space
   */
  actionsGroupBottom: _propTypes.default.bool,
  /**
   * second valueTag text
   */
  additionalOffersText: _propTypes.default.string,
  /**
   * Sets the backgroundColor of the card
   */
  backgroundColor: _propTypes.default.string,
  /**
   * top left badge text
   */
  badge: _propTypes.default.string,
  /**
   * Allows brand prop to accept an object
   * - ** text: ** Sets the text of the brand
   * - ** tagName: ** Sets the HTML tag of the brand oneOf( ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] )
   * - ** titleStyle: ** Sets the style of the brand oneOf( ['body-lg', 'body', 'body-sm', 'label', 'label-sm', 'title-xlg', 'title-lg', 'title', 'title-sm', 'headline-lg', 'headline', 'headline-sm', 'display-xlg', 'display-lg', 'display', 'display-sm', 'displayBody', 'displayBody-sm',] )
   */
  brandName: _propTypes.default.object,
  /**
   * Where the content block should align vertically
   * - **top:** top of available space
   * - **middle:** middle of available space
   * - **bottom:** bottom of available space
   */
  contentAlign: _propTypes.default.oneOf(['top', 'middle', 'bottom']),
  /**
   * The space between the body content and the action group
   * - **none:** 0 Sets no spacing
   * - **sm:** 8px Sets 8px spacing
   * - **md:** 16px Sets 16px spacing
   * - **lg:** 24px Sets 24px spacing
   * - **xl:** 32px Sets 32px spacing
   */
  contentGap: _propTypes.default.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  /**
   * The padding for the content of the card
   * option is not set, padding is 0
   * - **none:** 0 Sets no padding
   * - **default:** Sets to 16px on the sides and 8px on the bottom
   */
  contentPadding: _propTypes.default.oneOf(['none', 'default']),
  /**
   * Allows description prop to accept an object
   * - ** text: ** Sets the text of the brand
   * - ** titleStyle: ** Sets the style of the brand oneOf( ['body-lg', 'body', 'body-sm', 'label', 'label-sm', 'title-xlg', 'title-lg', 'title', 'title-sm', 'headline-lg', 'headline', 'headline-sm', 'display-xlg', 'display-lg', 'display', 'display-sm', 'displayBody', 'displayBody-sm',] )
   */
  description: _propTypes.default.object,
  /**
   * fully clickable card action from web-site
   */
  externalActionElement: _propTypes.default.object,
  /**
   * addToBagAction coming from web-site
   */
  externalFooterActionElement: _propTypes.default.object,
  /**
   * the externalMediaElement for the ProductCardDefault
   */
  externalMediaElement: _propTypes.default.object,
  /**
   * Smaller text above the title
   */
  eyebrow: _propTypes.default.string,
  /**
   * boolean if card can be fully clickable
   */
  fullyClickableParent: _propTypes.default.bool,
  /**
   * The background color for the header
   */
  headerBackgroundColor: _propTypes.default.string,
  /**
   * The padding between the header and the image
   * option is not set, padding is 0
   * - **none:** 0 Sets no padding
   * - **compact:** 16px padding all the way around
   * - **default:** 32px Sets 32px spacing on XL screen and 24 sides LG-SM
   * - **item:** Sets 16px padding all the way around
   */
  headerPadding: _propTypes.default.oneOf(['none', 'compact', 'default', 'item']),
  /**
   * Shows/Hides the footer
   */
  hideFooter: _propTypes.default.bool,
  /**
   * Where in the card the image aligns
   * - **top:** Aligns the image to the top of card
   * - **right:** Aligns the image to the right of card
   * - **bottom:** Aligns the image to the bottom of card
   * - **left:** Aligns the image to the left of card
   */
  imgAlign: _propTypes.default.oneOf(['top', 'right', 'bottom', 'left']),
  /**
   * The space between image and the content group
   * option is not set, padding is 0
   * - **none:** 0 Sets no spacing
   * - **sm:** 8px Sets 8px spacing
   * - **md:** 16px Sets 16px spacing
   * - **lg:** 24px Sets 24px spacing
   * - **xl:** 32px Sets 32px spacing
   */
  imgGap: _propTypes.default.oneOf(['none', 'sm', 'md', 'lg', 'xl']),
  /**
   * boolean for RailCards that have elevation to add more spacing
   */
  isElevated: _propTypes.default.bool,
  /**
   * The space between the edge of the card and the content
   * option is not set, padding is 0
   * - **none:** 0 Sets no padding
   * - **sm:** 16px Sets 16px padding
   * - **md:** 24px Sets 24px padding
   * - **lg:** 32px Sets 32px padding
   */
  padding: _propTypes.default.oneOf(['none', 'sm', 'md', 'lg']),
  /**
   * Object for Price
   *  - ** variant: **  Sets the variant for the Price oneOf(['default', 'PDP', 'large'])
   *  - ** listPrice: ** Sets the list price for the Price
   *  - ** listPriceAccessibilityLabel: ** Sets the list price accessibility label for screen readers
   *  - ** salePrice: **  Sets the sale price for the Price
   *  - ** salePriceAccessibilityLabel: ** Sets the sale price accessibility label for screen readers
   *  - ** kitPrice: **  Sets the kit price for the Price
   *  - ** kitPriceAccessibilityLabel: **  Sets the kit price accessibility label for screen readers
   *  - ** message: ** Sets the message to display when there is no price
   *  - ** disabled: ** Sets the disabled state for the price
   *  - ** priceLabel: ** Sets the price label to display
   *  - ** originalPriceLabel: ** Sets the original price text to display with list price
   */
  price: _propTypes.default.object,
  /**
   * Object for productVariant
   *  - ** externalSwatchImage: **  Sets the external swatch image
   *  - ** swatchImage: ** Sets the palette swatch image
   *  - ** variantTypeLabel: **  Sets the variant label coming from web-site
   *  - ** variantTypeDescription: **  Sets the variant description coming from web-site (called variantLabel in web-site)
   */
  productVariant: _propTypes.default.object,
  /**
   * Allows ratings prop to accept an object
   * - **rating:** Sets the Ratings stars
   * - **reviews:** Sets the reviews
   */
  ratings: _propTypes.default.object,
  /**
   * Text for the second value tag
   */
  secondValueTag: _propTypes.default.string,
  /**
   * Text for the sponsored badge label
   */
  sponsoredBadgeLabel: _propTypes.default.string,
  /**
   * Allows passing an object to the tag prop
   * See tag component for more detail ../Tag/Tag.jsx
   */
  tag: _propTypes.default.object,
  /**
   * Allows passing an array of tag objects to the ProductCardBody
   * See tag component for more detail ../Tag/Tag.jsx
   */
  tagsBottom: _propTypes.default.arrayOf(_propTypes.default.object),
  /**
   * Allows passing an array of tag objects to the ProductCardHeader
   * See tag component for more detail ../Tag/Tag.jsx
   */
  tagsTop: _propTypes.default.arrayOf(_propTypes.default.object),
  /**
   * Where the text content should align horizontally
   * - **left:** left side of available space
   * - **center:** center of available space
   * - **right:** right of available space
   */
  textAlign: _propTypes.default.oneOf(['left', 'center', 'right']),
  /**
   * Sets the theme for the backgroundColor
   */
  theme: _propTypes.default.string,
  /**
   * Allows title prop to accept an object
   * - ** text: ** Sets the title of the header
   * - ** tagName: ** Sets the HTML tag of the header oneOf( ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] )
   * - ** titleStyle: ** Sets the style of the header oneOf( ['body-lg', 'body', 'body-sm', 'label', 'label-sm', 'title-xlg', 'title-lg', 'title', 'title-sm', 'headline-lg', 'headline', 'headline-sm', 'display-xlg', 'display-lg', 'display', 'display-sm', 'displayBody', 'displayBody-sm',] )
   */
  title: _propTypes.default.object,
  /**
   * Support text for title
   */
  titleSupport: _propTypes.default.string,
  /**
   * Text for value tag
   */
  valueTag: _propTypes.default.string,
  /**
   * support text of the product card
   */
  variantLabel: _propTypes.default.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} backgroundColor='background-default' - sets the background color
 * @property {string} headerBackgroundColor='background-default' - sets the background color of the header
 * @property {string} theme='default' - sets the theme at the component level
 */
const defaultProps = exports.defaultProps = {
  backgroundColor: 'background-default',
  headerBackgroundColor: 'background-default',
  theme: 'default'
};
ProductCard.propTypes = propTypes;
var _default = exports.default = ProductCard;